import React from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  CartTransformFeaturesType,
  PatchConfigurationDto,
} from "@intelligems/ig-types";
import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTheme } from "@mui/styles";
import { usePatchConfigurations } from "@src/api/configurations/patch-configurations";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrgId } from "@src/api/organizations/get-organization";
import { useSyncPriceTestMetafields } from "@src/api/price-functions/sync-pricing-functions";
import { useGetCartTransforms } from "@src/api/shops/get-cart-transforms";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";

import { CartTransformAccordion } from "./CartTransformAccordion";
import { MetafieldLookupSection } from "./MetafieldLookupSection";

interface CartTransformsSectionProps {
  cartTransformFeatures: CartTransformFeaturesType;
}

const ColorText = ({ value }: { value: boolean }) => {
  const theme = useTheme();
  return (
    <IgTypography
      sx={{
        color: value
          ? theme.palette.text.tonal.success
          : theme.palette.text.tonal.error,
        fontWeight: 600,
      }}
    >
      {value ? "Allowed" : "Not Allowed"}
    </IgTypography>
  );
};

const CartTransformFeaturesSection = ({
  cartTransformFeatures,
}: CartTransformsSectionProps) => {
  return (
    <Grid container item direction={"column"} sx={{ marginBottom: "10px" }}>
      <Grid item>
        <IgTypography>
          Update Operation:{" "}
          <ColorText value={cartTransformFeatures?.updateOperation ?? false} />{" "}
          by Shopify
        </IgTypography>
      </Grid>
      <Grid item>
        <IgTypography>
          Expand Operation:{" "}
          <ColorText value={cartTransformFeatures?.expandOperation ?? false} />{" "}
          by Shopify
        </IgTypography>
      </Grid>
      <Grid item>
        <IgTypography>
          Merge Operation:{" "}
          <ColorText value={cartTransformFeatures?.mergeOperation} /> by Shopify
        </IgTypography>
      </Grid>
    </Grid>
  );
};

const ActiveModeSection = () => {
  const items = ["update", "expand", "merge"];
  const activeMode = useConfiguration<string>("cart_transform_mode")?.value;
  const [item, setItem] = useImmer<string>(activeMode || "update");
  const theme = useTheme();
  const mutateConfigurations = usePatchConfigurations();
  const organizationId = useGetOrgId();

  const onClick = () => {
    const data: PatchConfigurationDto = {
      value: item,
      type: "String",
      key: "cart_transform_mode",
      topic: "PLUGIN_CONFIG",
      organizationId,
    };

    mutateConfigurations.mutate([data]);
  };

  return (
    <Grid container item direction={"column"} rowSpacing={1}>
      <Grid item>
        <IgTypography variant={"h3"} fontWeight={"fontWeightMedium"}>
          Active Mode:
        </IgTypography>
      </Grid>
      <Grid container item columnSpacing={2}>
        <Grid item>
          <IgSelect
            value={item}
            onChange={(e: SelectChangeEvent) => setItem(e.target.value)}
          >
            {items.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </IgSelect>
        </Grid>
        <Grid item>
          <IgButton
            variant={"contained"}
            onClick={onClick}
            loading={mutateConfigurations.isPending}
          >
            Save
          </IgButton>
        </Grid>
      </Grid>
      <Grid item>
        <IgTypography variant={"body2"} color={theme.palette.text.secondary}>
          * In general, use update for Shopify Plus stores and expand for
          Non-Plus stores.
        </IgTypography>
      </Grid>
    </Grid>
  );
};

const CartTransformsSection = () => {
  const cartTransforms = useGetCartTransforms();
  const theme = useTheme();
  return (
    <Grid container item direction={"column"}>
      <Grid item sx={{ marginBottom: "10px" }}>
        <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
          Store Cart Transforms
        </IgTypography>
      </Grid>
      {cartTransforms.isSuccess ? (
        <Grid container item direction={"column"}>
          <CartTransformFeaturesSection
            cartTransformFeatures={cartTransforms.data.cartTransformFeatures}
          />
          {cartTransforms.data.cartTransforms?.length > 1 ? (
            <IgTypography variant={"body2"}>
              Intelligems only uses 1 cart transform function. Multiple Cart
              Transforms may exist from other apps.
            </IgTypography>
          ) : null}
          {cartTransforms.data.cartTransforms?.length > 0 ? (
            cartTransforms.data.cartTransforms.map((cartTransform, index) => (
              <CartTransformAccordion
                key={`${cartTransform.id}-${index}`}
                cartTransform={cartTransform}
                index={index}
                totalCount={cartTransforms.data.cartTransforms?.length}
              />
            ))
          ) : (
            <IgTypography
              color={theme.palette.text.tonal.error}
              sx={{ fontWeight: 600 }}
            >
              No Cart Transforms found
            </IgTypography>
          )}
        </Grid>
      ) : (
        <SkeletonGroup lines={3} />
      )}
    </Grid>
  );
};

export const PricingStatusSettingsSection = () => {
  const theme = useTheme();
  const syncPricingFunction = useSyncPriceTestMetafields();
  const pricingFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_for_pricing_enabled"
  );

  return (
    <Grid container direction={"column"} rowSpacing={3}>
      <Grid item>
        <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
          Manage and Troubleshoot Pricing Functions
        </IgTypography>
      </Grid>
      {pricingFunctionsEnabled !== undefined ? (
        <Grid item>
          <IgTypography>
            Pricing Functions Configuration{" "}
            <IgTypography
              sx={{
                fontWeight: 600,
                color: pricingFunctionsEnabled.value
                  ? theme.palette.text.tonal.success
                  : theme.palette.text.tonal.error,
              }}
            >
              {pricingFunctionsEnabled?.value ? "Enabled" : "Disabled"}
            </IgTypography>
          </IgTypography>
        </Grid>
      ) : null}
      <Grid item>
        <IgButton
          variant={"contained"}
          loading={syncPricingFunction.isPending}
          onClick={() => {
            syncPricingFunction.mutate();
            setTimeout(
              () => window.scrollTo({ top: 0, behavior: "smooth" }),
              1000
            );
          }}
        >
          Sync Pricing Function
        </IgButton>
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <IgDivider />
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <ActiveModeSection />
      </Grid>
      <Grid item xs>
        <CartTransformsSection />
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <IgDivider />
      </Grid>
      <Grid item xs>
        <MetafieldLookupSection
          type={"variant"}
          metafieldKey={"variationPrices"}
        />
      </Grid>
    </Grid>
  );
};
